import React, { useRef, useEffect } from 'react'
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box, Typography, FormControlLabel, Switch } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { FormFieldType } from '@/core/models/form.model'
import { Trans, t } from '@lingui/macro'
import FormTemplateFieldRow from './FormTemplateFieldRow'
import { parseTagsInput, withChecklistCategory } from '../lib/formTemplateClassification'

interface Field {
  label: string
  type: FormFieldType
  required: boolean
  options?: string[]
}

interface CreateFormTemplateDialogProps {
  open: boolean
  onClose: () => void
  onSubmit: (data: { title: string; description: string; fields: Field[]; categories?: string[]; tags?: string[] }) => void
  isSubmitting?: boolean
}

const CreateFormTemplateDialogComponent: React.FC<CreateFormTemplateDialogProps> = ({ open, onClose, onSubmit, isSubmitting = false }) => {
  const [title, setTitle] = React.useState('')
  const [description, setDescription] = React.useState('')
  const [fields, setFields] = React.useState<Field[]>([])
  // A "preparation checklist" is just a FormTemplate carrying the readiness-checklist category —
  // that category is exactly what the waiting-room readiness dropdown filters on.
  const [isChecklist, setIsChecklist] = React.useState(false)
  const [tags, setTags] = React.useState('')
  const newFieldRef = useRef<HTMLDivElement>(null)
  const shouldScrollToNewField = useRef(false)

  const handleAddField = () => {
    shouldScrollToNewField.current = true
    setFields([
      ...fields,
      {
        label: '',
        type: FormFieldType.Text,
        required: false,
        options: [],
      },
    ])
  }

  const handleRemoveField = (index: number) => {
    setFields(fields.filter((_, i) => i !== index))
  }

  useEffect(() => {
    if (shouldScrollToNewField.current && newFieldRef.current) {
      newFieldRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      shouldScrollToNewField.current = false
    }
  }, [fields])

  const handleFieldChange = (index: number, field: Partial<Field>) => {
    setFields(
      fields.map((f, i) => {
        if (i !== index) return f

        const updatedField = { ...f, ...field }

        // Initialize or clear options when changing type
        if (field.type) {
          if (field.type === FormFieldType.Multiple) {
            updatedField.options = updatedField.options || []
          } else {
            updatedField.options = undefined
          }
        }

        return updatedField
      })
    )
  }

  const handleSubmit = () => {
    onSubmit({
      title,
      description,
      fields: fields.map((field) => ({ ...field, options: field.options?.map((option) => option.trim()).filter((option) => option.length > 0) })),
      categories: withChecklistCategory([], isChecklist),
      tags: parseTagsInput(tags),
    })
  }

  const handleClose = () => {
    setTitle('')
    setDescription('')
    setFields([])
    setIsChecklist(false)
    setTags('')
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{t`Create new form template`}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <TextField fullWidth label={t`Form title`} value={title} onChange={(e) => setTitle(e.target.value)} sx={{ mb: 2 }} />
          <TextField
            fullWidth
            label={t`Form description`}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            rows={2}
            sx={{ mb: 2 }}
          />

          <FormControlLabel
            control={<Switch checked={isChecklist} onChange={(e) => setIsChecklist(e.target.checked)} />}
            label={<Trans>Use as a waiting-room preparation checklist</Trans>}
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            <Trans>Checklist templates can be selected in a waiting room's readiness settings, so operators can tick items before the doctor joins.</Trans>
          </Typography>

          <TextField
            fullWidth
            label={t`Tags`}
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            placeholder={t`e.g. cardiology, generalist`}
            helperText={t`Comma-separated. Optional.`}
            sx={{ mb: 3 }}
          />

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              <Trans>Fields</Trans>
            </Typography>
          </Box>

          {fields.map((field, index) => (
            <Box key={index} ref={index === fields.length - 1 && shouldScrollToNewField.current ? newFieldRef : null}>
              <FormTemplateFieldRow field={field} index={index} onChange={handleFieldChange} onRemove={handleRemoveField} />
            </Box>
          ))}

          <Button startIcon={<AddIcon />} onClick={handleAddField} variant="outlined" size="small" sx={{ mt: 1 }}>
            <Trans>Add field</Trans>
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}> {t`Cancel`}</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={isSubmitting || !title || fields.length === 0}>
          {isSubmitting ? t`Saving...` : t`Save`}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default CreateFormTemplateDialogComponent
